import React, { useEffect, useState } from 'react';
import {
	Box,
	Flex,
	Heading,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	HStack,
	ButtonGroup,
	Button,
	SimpleGrid,
	Input,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	FormControl,
	FormLabel,
	Textarea,
	Image,
	useToast,
	Text,
	Select,
	Stack,
	Spinner,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, SearchIcon } from '@chakra-ui/icons';
import { connect } from 'react-redux';
import {
	getAllCategories,
	createCategory,
	updateCategory,
	deleteCategory,
} from '../../redux/actions/categoryActions';
import {
	useReactTable,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
} from '@tanstack/react-table';
import AdminNavbar from '../../component/adminNavbar';
import Alert from '../../component/alert';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const Categories = ({
	getAllCategories,
	Categories,
	loading,
	createCategory,
	updateCategory,
	deleteCategory,
}) => {
	useEffect(() => {
		getAllCategories();
	}, []);

	const [searchData, setSearchData] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [formState, setFormState] = useState({
		category_name: '',
		description: '',
		parent_id: '',
	});
	const [photo, setPhoto] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const toast = useToast();

	const handleSearchClick = () => {
		setSearchData((v) => v.trim());
		setIsSearching(true);
	};

	const handleClearClick = () => {
		setSearchData('');
		setIsSearching(true);
	};

	const handleOpenDeleteModal = (category) => {
		setSelectedCategory(category);
		setIsDeleteModalOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setSelectedCategory(null);
		setIsDeleteModalOpen(false);
	};

	const handleConfirm = () => {
		deleteCategory(selectedCategory.category_id);
		handleCloseDeleteModal();
		getAllCategories();
	};

	const handleOpenModal = (category?) => {
		if (category) {
			setSelectedCategory(category);
			setFormState({ ...category, parent_id: category.parent_id || '' });
			if (category.image) {
				setImagePreview(`${BASE_URL}/api/image?filename=${category.image}`);
			}
		} else {
			setSelectedCategory(null);
			setFormState({ category_name: '', description: '', parent_id: '' });
			setPhoto(null);
			setImagePreview(null);
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedCategory(null);
		setPhoto(null);
		if (imagePreview && imagePreview.startsWith('blob:')) {
			URL.revokeObjectURL(imagePreview);
		}
		setImagePreview(null);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormState((prev) => ({ ...prev, [name]: value }));
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setPhoto(file);
			if (imagePreview && imagePreview.startsWith('blob:')) {
				URL.revokeObjectURL(imagePreview);
			}
			setImagePreview(URL.createObjectURL(file));
		}
	};

	const handleSave = async () => {
		const formData = new FormData();
		formData.append('category_name', formState.category_name);
		formData.append('description', formState.description);
		if (formState.parent_id) {
			formData.append('parent_id', formState.parent_id);
		}
		if (photo) {
			formData.append('photo', photo);
		}

		try {
			if (selectedCategory) {
				await updateCategory(selectedCategory.category_id, formData);
				toast({
					title: 'Category updated.',
					status: 'success',
					duration: 5000,
					isClosable: true,
				});
			} else {
				await createCategory(formData);
				toast({
					title: 'Category created.',
					status: 'success',
					duration: 5000,
					isClosable: true,
				});
			}
			handleCloseModal();
			getAllCategories();
		} catch (error) {
			toast({
				title: 'An error occurred.',
				description: `Unable to ${selectedCategory ? 'update' : 'create'} category.`,
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const columnHelper = createColumnHelper<any>();
	const columns = [
		columnHelper.accessor('category_id', {
			header: 'Category ID',
			cell: (info) => info.getValue(),
		}),
		columnHelper.accessor('category_name', {
			header: 'Category Name',
			cell: (info) => info.getValue(),
		}),
		columnHelper.accessor('description', {
			header: 'Description',
			cell: (info) => info.getValue(),
		}),
		columnHelper.accessor('parent_id', {
			header: 'Parent Category',
			cell: (info) => {
				const parentId = info.getValue();
				const parent = Categories?.find((c) => c.category_id === parentId);
				return parent ? parent.category_name : 'None';
			},
		}),
		columnHelper.display({
			id: 'action',
			header: 'Actions',
			cell: ({ row }) => (
				<HStack spacing={2}>
					<ButtonGroup size="xs">
						<Button
							colorScheme="blue"
							onClick={() => handleOpenModal(row.original)}
						>
							<EditIcon />
						</Button>
						<Button
							colorScheme="red"
							onClick={() => handleOpenDeleteModal(row.original)}
						>
							<DeleteIcon />
						</Button>
					</ButtonGroup>
				</HStack>
			),
		}),
	];

	const table = useReactTable({
		columns,
		data: Categories || [],
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<>
			<Alert />
			<AdminNavbar />
			{!loading ? (
				<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.50" minH="100vh">
					<Heading mb={6} fontSize="2xl" fontWeight="bold" color="gray.700">
						Categories
					</Heading>

					<Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
							<Input
								placeholder="Category Name"
								size="sm"
								borderRadius="md"
								value={searchData}
								onChange={(e) => setSearchData(e.target.value)}
								isDisabled={loading}
							/>
						</SimpleGrid>
						<Flex justify="space-between" flexWrap="wrap" gap={2}>
							<Button
								size="sm"
								colorScheme="green"
								leftIcon={<AddIcon />}
								onClick={() => handleOpenModal()}
								variant="outline"
							>
								Create Category
							</Button>
							<HStack spacing={2}>
								<Button
									size="sm"
									variant="ghost"
									colorScheme="gray"
									onClick={handleClearClick}
									isDisabled={loading}
								>
									Clear Filters
								</Button>
								<Button
									size="sm"
									colorScheme="blue"
									onClick={handleSearchClick}
									isLoading={isSearching}
									leftIcon={<SearchIcon />}
								>
									Search
								</Button>
							</HStack>
						</Flex>
					</Box>

					<Box borderRadius="lg" boxShadow="md" overflowX="auto" bg="white">
						<Table variant="simple" colorScheme="gray" size="sm">
							<Thead bg="gray.100">
								{table.getHeaderGroups().map((headerGroup) => (
									<Tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<Th key={header.id}>
												{flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
											</Th>
										))}
									</Tr>
								))}
							</Thead>
							<Tbody>
								{table.getRowModel().rows.map((row) => (
									<Tr
										key={row.id}
										_hover={{ bg: 'blue.100' }}
										transition="background 0.2s ease"
									>
										{row.getVisibleCells().map((cell) => (
											<Td key={cell.id} fontSize="sm" py={2}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</Td>
										))}
									</Tr>
								))}
							</Tbody>
						</Table>
					</Box>

					{/* Edit / Create Modal */}
					<Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>
								{selectedCategory ? 'Edit Category' : 'Create Category'}
							</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								<Stack spacing={4}>
									<FormControl>
										<FormLabel>Category Name</FormLabel>
										<Input
											name="category_name"
											value={formState.category_name}
											onChange={handleChange}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Category Description</FormLabel>
										<Textarea
											name="description"
											value={formState.description}
											onChange={handleChange}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Parent Category</FormLabel>
										<Select
											name="parent_id"
											value={formState.parent_id}
											onChange={handleChange}
											placeholder="Select parent category"
										>
											{Categories?.filter(
												(c) => c.category_id !== formState.category_id
											).map((cat) => (
												<option key={cat.category_id} value={cat.category_id}>
													{cat.category_name}
												</option>
											))}
										</Select>
									</FormControl>
									<FormControl>
										<FormLabel>Category Image</FormLabel>
										<Input
											name="category_image"
											type="file"
											onChange={handleFileChange}
											accept="image/*"
										/>
									</FormControl>
									{imagePreview && (
										<FormControl>
											<FormLabel>Image Preview</FormLabel>
											<Image
												src={imagePreview}
												alt="Image preview"
												boxSize="150px"
												objectFit="cover"
												borderRadius="md"
											/>
										</FormControl>
									)}
								</Stack>
							</ModalBody>
							<ModalFooter>
								<Button colorScheme="blue" mr={3} onClick={handleSave}>
									{selectedCategory ? 'Update' : 'Save'}
								</Button>
								<Button variant="ghost" onClick={handleCloseModal}>
									Cancel
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>

					{/* Delete Confirmation Modal */}
					<Modal isOpen={isDeleteModalOpen} onClose={handleCloseDeleteModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>Delete Category</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								Are you sure you want to delete{' '}
								<strong>{selectedCategory?.category_name}</strong>?
							</ModalBody>
							<ModalFooter>
								<Button mr={3} onClick={handleCloseDeleteModal}>
									Cancel
								</Button>
								<Button colorScheme="red" onClick={handleConfirm}>
									Delete
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>
				</Box>
			) : (
				<Box ml={{ base: 0, md: "250px" }} p={10} display="flex" justifyContent="center">
					<Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
				</Box>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	Categories: state.categories.categories,
	loading: state.categories.loading,
});

const mapDispatchToProps = {
	getAllCategories,
	createCategory,
	updateCategory,
	deleteCategory,
};

export default connect(mapStateToProps, mapDispatchToProps)(Categories);
